<template>
  <div class="standard-announcements-page">
    <Navbar />
    <PageBanner title="標準公告區" />

    <!-- Breadcrumb Area -->
    <Breadcrumb :items="breadcrumbItems" />

    <div class="content">
      <!-- Filters -->
      <div class="filters-container">
        <div class="filter-item">
          <div class="search-input-wrap">
            <span class="search-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="6 10 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18.9167 21.6667H18.2583L18.025 21.4417C18.8417 20.4917 19.3333 19.2583 19.3333 17.9167C19.3333 14.925 16.9083 12.5 13.9167 12.5C10.925 12.5 8.5 14.925 8.5 17.9167C8.5 20.9083 10.925 23.3333 13.9167 23.3333C15.2583 23.3333 16.4917 22.8417 17.4417 22.025L17.6667 22.2583V22.9167L21.8333 27.075L23.075 25.8333L18.9167 21.6667ZM13.9167 21.6667C11.8417 21.6667 10.1667 19.9917 10.1667 17.9167C10.1667 15.8417 11.8417 14.1667 13.9167 14.1667C15.9917 14.1667 17.6667 15.8417 17.6667 17.9167C17.6667 19.9917 15.9917 21.6667 13.9167 21.6667Z"
                  fill="#696969" />
              </svg>
            </span>
            <input type="text" v-model.trim="searchQuery" placeholder="關鍵字" aria-label="搜尋標準公告關鍵字" title="搜尋標準公告關鍵字" />
          </div>
        </div>
        <div class="filter-item">
          <div class="custom-select-wrap">
            <span class="select-label">類別</span>
            <span class="select-divider">|</span>
            <select v-model="category" class="select-input" aria-label="篩選標準公告類別" title="篩選標準公告類別">
              <option value="all">全部</option>
              <option v-for="opt in categoryOptions" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
            <span class="select-arrow" aria-hidden="true">
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </span>
          </div>
        </div>
        <div class="filter-item">
          <div class="custom-select-wrap">
            <span class="select-label">單位</span>
            <span class="select-divider">|</span>
            <select v-model="unit" class="select-input" aria-label="篩選標準公告發布單位" title="篩選標準公告發布單位">
              <option value="all">全部</option>
              <option v-for="opt in unitOptions" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
            <span class="select-arrow" aria-hidden="true">
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </span>
          </div>
        </div>

        <!-- API 金鑰申請按鈕 -->
        <div class="filter-item-right" style="
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 8px;
          ">
          <a-button v-if="applicationStatus === 'approved'" type="primary" @click="goToMyApiKey">
            我的API金鑰
          </a-button>
          <a-button v-else-if="applicationStatus === 'pending'" type="default" disabled>
            API金鑰申請中
          </a-button>
          <a-button v-else type="primary" @click="handleApplyClick">
            申請 API 串接金鑰
          </a-button>
        </div>
      </div>

      <!-- Scroll Hint on Mobile -->
      <div v-if="isScrollable" class="scroll-hint desktop-only">
        <svg class="scroll-hint-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
        <span>左右滑動以瀏覽更多資訊</span>
      </div>

      <div class="materials-content desktop-only" ref="contentRef"
        :data-can-scroll-right="canScrollRight ? 'true' : 'false'">
        <table class="materials-table">
          <thead>
            <tr>
              <th class="col-spec">標準名稱</th>
              <th class="col-cat">類別</th>
              <th class="col-unit">單位</th>
              <th class="col-release">發佈版本</th>
              <th class="col-link">使用說明</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="5">載入中...</td>
            </tr>
            <tr v-else-if="errorMessage">
              <td colspan="5">{{ errorMessage }}</td>
            </tr>
            <tr v-else-if="tableData.length === 0">
              <td colspan="5">尚無資料</td>
            </tr>
            <tr v-else v-for="(item, index) in tableData" :key="index">
              <td>{{ item.spec }}</td>
              <td>
                <div class="cat-zh">{{ item.catZh }}</div>
                <div class="cat-en" v-if="item.catEn">{{ item.catEn }}</div>
              </td>
              <td>{{ item.unit || "-" }}</td>
              <td>
                <div class="release-version">{{ item.releaseVersion }}</div>
                <div v-if="item.publishedAt" class="published-at">
                  {{ item.publishedAt }}
                </div>
              </td>
              <td>
                <!-- 若為資料集公告，顯示使用說明按鈕 -->
                <a-button v-if="
                  item.sourceFilePath && item.importStatus === 'completed'
                " type="link" class="use-guide-btn" @click="openUseGuide(item)">
                  使用說明
                </a-button>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile loading/error/empty states -->
      <div class="mobile-only mobile-state-placeholder" v-if="isLoading || errorMessage || tableData.length === 0">
        <div v-if="isLoading">載入中...</div>
        <div v-else-if="errorMessage">{{ errorMessage }}</div>
        <div v-else-if="tableData.length === 0">尚無資料</div>
      </div>

      <!-- Mobile Cards View -->
      <div class="materials-cards mobile-only" v-else>
        <MobileCard v-for="(item, index) in tableData" :key="index" :title="item.spec" :rows="[
          { label: '類別', key: 'category' },
          { label: '單位', value: item.unit || '-', key: 'unit' },
          { label: '發佈版本', key: 'releaseVersion' },
          { label: '使用說明', key: 'link' },
        ]">
          <template #category>
            <div class="cat-zh">{{ item.catZh }}</div>
            <div class="cat-en" v-if="item.catEn">{{ item.catEn }}</div>
          </template>
          <template #releaseVersion>
            <div class="release-version">{{ item.releaseVersion }}</div>
            <div v-if="item.publishedAt" class="published-at">
              {{ item.publishedAt }}
            </div>
          </template>
          <template #link>
            <a-button v-if="item.sourceFilePath && item.importStatus === 'completed'" type="link" class="use-guide-btn"
              style="padding: 0; height: auto" @click="openUseGuide(item)">
              使用說明
            </a-button>
            <span v-else>-</span>
          </template>
        </MobileCard>
      </div>
    </div>

    <!-- 免責聲明 Modal -->
    <a-modal v-model:open="isDisclaimerOpen" title="發行套件授權" width="760px" :ok-text="'我同意這個授權'" :cancel-text="'取消'"
      @ok="acceptDisclaimer" @cancel="isDisclaimerOpen = false">
      <div class="disclaimer-content">
        <p>請在下載發行包前先審閱並接受以下授權條款：</p>
        <p><strong>免責聲明：</strong></p>
        <p>
          您即將下載的檔案是由外部來源提供，這些來源不受我們控制。我們無法保證這些外部來源所提供內容的準確性、完整性或安全性。繼續下載即表示您承認並同意以下事項：
        </p>
        <ol>
          <li>
            <strong>無保固：</strong>檔案以「現狀」提供，沒有任何明示或暗示的保證，包括但不限於對適銷性、特定用途適宜性或非侵權的默示保證。
          </li>
          <li>
            <strong>安全風險：</strong>從外部來源下載檔案本身就有風險，包括但不限於可能遭受病毒、惡意軟體或其他惡意軟體的可能。我們建議您使用適當且最新的防毒與安全軟體來保護您的裝置。
          </li>
          <li>
            <strong>責任：</strong>您對因下載或使用這些檔案而可能造成的裝置損壞或資料遺失，負全責。我們對您因下載或使用這些檔案而產生的任何損害、損害或損失，概不負責。
          </li>
          <li>
            <strong>外部連結：</strong>提供外部網站連結以方便您，並不代表我們對這些網站內容、產品或服務的背書。我們不對
            these 外部網站的內容或隱私措施負責。
          </li>
          <li>
            <strong>更新與變更：</strong>檔案內容及提供這些檔案的外部來源可能會在未通知的情況下變更。我們不負責保持檔案的最新性或確保其持續可用。
          </li>
        </ol>
        <p>
          下載即表示您同意這些條款與條件。如果您不同意本免責聲明的任何部分，請勿下載這些檔案。
        </p>
      </div>
    </a-modal>

    <!-- 使用指南 Modal -->
    <a-modal v-model:open="isApiDocsOpen" title="資料集查詢使用指南" :footer="null" width="760px">
      <div class="api-docs-content">
        <!-- 未登入提示 -->
        <div v-if="!isLoggedIn" class="login-prompt">
          <div class="prompt-icon">
            <lock-outlined />
          </div>
          <p class="prompt-intro">
            歡迎使用資料集查詢服務！本系統提供
            <strong>網頁線上查詢</strong> 與
            <strong>API 串接</strong>
            兩種方式。在使用任何查詢功能前，請先完成會員註冊。
          </p>

          <div class="guide-section">
            <h3>事前準備：註冊會員</h3>
            <p>不論使用哪一種查詢方式，皆須具備會員身分：</p>
            <ol>
              <li>
                進入首頁後，請先點選右上角進行 <strong>會員註冊</strong>。
              </li>
              <li>註冊成功並登入後，即可開始使用以下查詢服務。</li>
            </ol>
          </div>

          <div class="guide-section">
            <h3>方式一：網頁線上查詢</h3>
            <p>預計七月底開放。</p>
          </div>

          <div class="guide-section" style="display:none;">
            <h3>方式二：API 串接查詢</h3>
            <p>
              適合開發者或有自動化、系統整合需求的使用者。您可以透過標準的 HTTP
              方法與指定網址來獲取資料。
            </p>
          </div>

          <div class="prompt-actions" style="margin-top: 24px; text-align: center">
            <a-button type="primary" size="large" @click="goToLogin" style="margin-right: 12px">
              立即登入
            </a-button>
            <a-button size="large" @click="goToRegister"> 立即註冊 </a-button>
          </div>
        </div>

        <!-- 已登入顯示 API 文件 -->
        <template v-else>
          <div class="guide-section">
            <h3 style="
                border-left: 4px solid #022964;
                padding-left: 8px;
                margin-bottom: 16px;
              ">
              方式一：API 串接查詢
            </h3>
            <p>
              適合開發者進行自動化與系統整合。請使用以下 API Endpoint
              並帶入指定版本參數：
            </p>
          </div>

          <p class="api-docs-label" style="margin-top: 12px">Endpoint</p>
          <pre><span class="api-docs-method">[GET]</span> {{ apiEndpoint }}</pre>

          <p class="api-docs-label">Header</p>
          <pre>Authorization: Bearer {apiKey}</pre>

          <p class="api-docs-label">支援參數</p>
          <table class="api-docs-table">
            <thead>
              <tr>
                <th>參數名稱</th>
                <th>型別</th>
                <th>說明</th>
                <th>範例</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="param in apiParameters" :key="param.name">
                <td>
                  <code>{{ param.name }}</code>
                </td>
                <td>{{ param.type }}</td>
                <td>{{ param.description }}</td>
                <td>
                  <code>{{ param.example }}</code>
                </td>
              </tr>
            </tbody>
          </table>

          <p class="api-docs-label">回傳格式（成功）</p>
          <pre>{{ apiResponseExample }}</pre>

          <p class="api-docs-label">限流說明</p>
          <p style="margin-bottom: 24px">
            每個 apiKey 每分鐘最多可請求 120
            次，超限時會回傳「請求過於頻繁，請稍候再試。」。
          </p>

          <div class="guide-section" style="display: none; border-top: 1px solid #eaeaea; padding-top: 16px">
            <h3 style="
                border-left: 4px solid #022964;
                padding-left: 8px;
                margin-bottom: 12px;
              ">
              方式二：網頁線上查詢
            </h3>
            <p style="color: #666; font-weight: 500">
              網頁線上直接查詢介面預計七月底開放，敬請期待。
            </p>
          </div>
        </template>
      </div>
    </a-modal>

    <!-- 申請 API 串接金鑰 Modal -->
    <a-modal v-model:open="isApplyModalOpen" title="填寫下載資訊" width="760px" :footer="null" :destroyOnClose="true">
      <div class="apply-form-content" style="max-height: 70vh; overflow-y: auto; padding: 10px 20px">
        <a-form layout="vertical" @submit.prevent="submitApplicationForm">
          <!-- 真實姓名 -->
          <a-form-item label="真實姓名*" required>
            <a-input v-model:value="applyForm.realName" placeholder="請輸入您的姓名" />
          </a-form-item>

          <!-- 所屬單位/組織 -->
          <a-form-item label="您的所屬單位/組織*" required>
            <a-input v-model:value="applyForm.organization" placeholder="請輸入單位名稱" />
          </a-form-item>

          <!-- 您的職業 -->
          <a-form-item label="您的職業*" required>
            <a-radio-group v-model:value="applyForm.jobTitle" style="display: flex; flex-direction: column; gap: 8px">
              <a-radio value="研究人員/教育工作者">研究人員/教育工作者</a-radio>
              <a-radio value="醫生">醫生</a-radio>
              <a-radio value="行政人員/經理">行政人員/經理</a-radio>
              <a-radio value="開發人員/工程師">開發人員/工程師</a-radio>
              <a-radio value="護理人員">護理人員</a-radio>
              <a-radio value="學生">學生</a-radio>
              <a-radio value="other">
                其他
                <a-input v-if="
                  applyForm.jobTitle === 'other' ||
                  ![
                    '研究人員/教育工作者',
                    '醫生',
                    '行政人員/經理',
                    '開發人員/工程師',
                    '護理人員',
                    '學生',
                  ].includes(applyForm.jobTitle)
                " :value="[
                  '研究人員/教育工作者',
                  '醫生',
                  '行政人員/經理',
                  '開發人員/工程師',
                  '護理人員',
                  '學生',
                ].includes(applyForm.jobTitle)
                  ? ''
                  : applyForm.jobTitle
                  " @change="(e) => (applyForm.jobTitle = e.target.value)" placeholder="請說明"
                  style="width: 200px; margin-left: 8px" />
              </a-radio>
            </a-radio-group>
          </a-form-item>

          <!-- 在組織中使用的模式 -->
          <a-form-item :label="`在您的組織中使用 ${STANDARD === 'loinc'
            ? 'LOINC'
            : STANDARD === 'rxnorm'
              ? 'RxNorm'
              : 'SNOMED CT'
            } 的模式*`" required>
            <a-radio-group v-model:value="applyForm.useMode" style="display: flex; flex-direction: column; gap: 8px">
              <a-radio value="產品化">產品化</a-radio>
              <a-radio value="臨床/研究">臨床/研究</a-radio>
              <a-radio value="原型/開發">原型/開發</a-radio>
              <a-radio value="其他">其他</a-radio>
            </a-radio-group>
          </a-form-item>

          <!-- 主要用途 -->
          <a-form-item :label="`使用 ${STANDARD === 'loinc'
            ? 'LOINC'
            : STANDARD === 'rxnorm'
              ? 'RxNorm'
              : 'SNOMED CT'
            } 的主要用途 (可複選)*`" required>
            <a-checkbox-group v-model:value="applyForm.purposes"
              style="display: flex; flex-direction: column; gap: 8px">
              <a-checkbox value="了解術語內容，尚未在臨床或操作環境中實施">了解術語內容，尚未在臨床或操作環境中實施</a-checkbox>
              <a-checkbox value="提取子集，將合適的術語值集整合至 FHIR 的應用中">提取子集，將合適的術語值集整合至 FHIR 的應用中</a-checkbox>
              <a-checkbox value="嵌入至第三方系統/軟體">嵌入至第三方系統/軟體</a-checkbox>
              <a-checkbox value="進行資料內容分類或編碼">進行資料內容分類或編碼</a-checkbox>
              <a-checkbox value="概念檢索及資料分析">概念檢索及資料分析</a-checkbox>
              <a-checkbox value="其他">其他</a-checkbox>
            </a-checkbox-group>
          </a-form-item>

          <!-- 預計使用系統 -->
          <a-form-item :label="`預計使用 ${STANDARD === 'loinc'
            ? 'LOINC'
            : STANDARD === 'rxnorm'
              ? 'RxNorm'
              : 'SNOMED CT'
            } 的系統 (可複選)*`" required>
            <a-checkbox-group v-model:value="applyForm.systems" style="display: flex; flex-direction: column; gap: 8px">
              <a-checkbox value="HIS/EMR">HIS/EMR</a-checkbox>
              <a-checkbox value="決策支持">決策支持</a-checkbox>
              <a-checkbox value="HIE">HIE</a-checkbox>
              <a-checkbox value="PHR">PHR</a-checkbox>
              <a-checkbox value="專科專用">專科專用</a-checkbox>
              <a-checkbox value="RIS">RIS</a-checkbox>
              <a-checkbox value="資料庫資訊檢索">資料庫資訊檢索</a-checkbox>
              <a-checkbox value="其他">其他</a-checkbox>
            </a-checkbox-group>
          </a-form-item>

          <!-- NDA 文件下載與上傳 -->
          <div class="nda-section" style="
              border: 1px solid #eaeaea;
              border-radius: 8px;
              padding: 16px;
              margin: 24px 0;
              background-color: #fafafa;
            ">
            <h4 style="margin-top: 0; margin-bottom: 12px; font-weight: 600">
              保密協定 (NDA) 簽署檔案上傳*
            </h4>
            <p style="font-size: 13px; color: #666; margin-bottom: 16px">
              請先下載空白 NDA
              文件，列印並親筆簽署完成並用印後後，將掃描檔或照片上傳接收。
            </p>
            <div style="
                display: flex;
                align-items: center;
                gap: 16px;
                margin-bottom: 16px;
              ">
              <a href="/apply_nda.docx" download class="ant-btn ant-btn-default"
                style="display: inline-flex; align-items: center; gap: 6px">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                下載空白 NDA 授權書 (.docx)
              </a>
            </div>
            <div>
              <input type="file" ref="fileInputRef" style="display: none" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                @change="handleFileChange" />
              <a-button type="dashed" @click="() => fileInputRef.click()">
                選擇已簽署 the NDA 檔案
              </a-button>
              <span v-if="selectedFileName" style="
                  margin-left: 12px;
                  color: #022964;
                  font-size: 14px;
                  font-weight: 500;
                ">
                已選擇: {{ selectedFileName }}
              </span>
            </div>
          </div>

          <!-- 免責聲明 -->
          <div class="terms-section" style="
              border: 1px solid #eaeaea;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 24px;
              max-height: 150px;
              overflow-y: auto;
              background-color: #fafafa;
              font-size: 13px;
              color: #555;
            ">
            <p><strong>免責聲明：</strong></p>
            <p>繼續下載與串接即表示您承認並同意以下事項：</p>
            <p>
              1. <strong>無保固：</strong>本平台對檔案或 API
              存取以「現狀」提供，沒有任何明示或暗示的保證。
            </p>
            <p>
              2. <strong>安全風險：</strong>您對因使用此 API
              金鑰而可能造成的裝置損壞或資料遺失負全責。
            </p>
            <p>
              3.
              <strong>責任：</strong>我們對您因使用金鑰而產生的任何損害、損害或損失概不負責。
            </p>
          </div>

          <div style="margin-bottom: 24px">
            <a-checkbox v-model:checked="applyForm.isNdaChecked">我同意上述條款</a-checkbox>
          </div>

          <!-- 按鈕 -->
          <div style="
              text-align: right;
              border-top: 1px solid #eee;
              padding-top: 16px;
            ">
            <a-button style="margin-right: 12px" @click="isApplyModalOpen = false">取消</a-button>
            <a-button type="primary" html-type="submit" :loading="applySubmitting">確認送出</a-button>
          </div>
        </a-form>
      </div>
    </a-modal>

    <FooterSection />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useLayoutStore } from "@/stores/layout";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { contentAPI, authAPI, authStorage, API_URL } from "@/services/api";
import MobileCard from "@/components/MobileCard.vue";
import { LockOutlined } from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";

const layoutStore = useLayoutStore();
const router = useRouter();

const breadcrumbItems = [
  { label: "首頁", path: "/" },
  { label: "臺灣標準" },
  { label: "標準公告區" },
];

const STANDARD = "snomed_ct";

const searchQuery = ref("");
const category = ref("all");
const unit = ref("all");

const isLoading = ref(true);
const errorMessage = ref("");

const announcements = ref([]);

const contentRef = ref(null);
const canScrollRight = ref(false);
const isScrollable = ref(false);
let resizeObserver = null;

// Modal 相關狀態
const isApiDocsOpen = ref(false);
const isDisclaimerOpen = ref(false);
const selectedAnnouncement = ref(null);
const isLoggedIn = ref(!!authStorage.getToken());

// API 金鑰申請相關狀態
const isApplyModalOpen = ref(false);
const applicationStatus = ref("none"); // none, pending, approved, rejected, unauthenticated
const reviewComment = ref("");
const fileInputRef = ref(null);

const applyForm = ref({
  realName: "",
  jobTitle: "",
  organization: "",
  useMode: "",
  purposes: [],
  systems: [],
  ndaFile: null,
  isNdaChecked: false,
});

const selectedFileName = ref("");
const applySubmitting = ref(false);

const fetchApplicationStatus = async () => {
  if (!isLoggedIn.value) {
    applicationStatus.value = "unauthenticated";
    return;
  }
  try {
    const res = await contentAPI.getApplicationStatus();
    applicationStatus.value = res?.data?.status || "none";
    reviewComment.value = res?.data?.review_comment || "";
  } catch (err) {
    console.error("Failed to fetch application status", err);
  }
};

const handleApplyClick = async () => {
  if (!isLoggedIn.value) {
    goToLogin();
    return;
  }

  let user = null;
  try {
    const meRes = await authAPI.me();
    if (meRes?.data) {
      user = meRes.data;
      localStorage.setItem("user_data", JSON.stringify(user));
    }
  } catch (e) {
    const rawUser = localStorage.getItem("user_data");
    try {
      user = rawUser ? JSON.parse(rawUser) : null;
    } catch (err) { }
  }

  const isAgreed = user?.standards_terms_agreed ? user.standards_terms_agreed['snomed_ct'] : user?.additional_terms_agreed;
  if (!isAgreed) {
    message.warning("進行 API 申請前，請先同意最新版附加條款");
    router.push({
      path: "/additional-terms",
      query: { redirect: router.currentRoute.value.fullPath },
    });
    return;
  }

  try {
    const res = await contentAPI.getApplicationProfile();
    const profile = res?.data || {};
    applyForm.value.realName = profile?.real_name || "";
    applyForm.value.jobTitle = profile?.job_title || "";
    applyForm.value.organization = profile?.organization || "";
  } catch (err) {
    console.error("Failed to fetch application profile", err);
  }

  isApplyModalOpen.value = true;
};

const goToMyApiKey = () => {
  const url = router.resolve({ name: "ProfileEdit" }).href;
  window.open(url + "#api-key-section", "_blank");
};

const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    applyForm.value.ndaFile = file;
    selectedFileName.value = file.name;
  }
};

const submitApplicationForm = async (forceSubmit = false) => {
  if (!applyForm.value.realName) {
    message.error("請輸入真實姓名");
    return;
  }
  if (!applyForm.value.organization) {
    message.error("請輸入單位名稱");
    return;
  }
  if (!applyForm.value.jobTitle) {
    message.error("請選擇或輸入您的職業");
    return;
  }
  if (!applyForm.value.useMode) {
    message.error("請選擇組織中使用的模式");
    return;
  }
  if (applyForm.value.purposes.length === 0) {
    message.error("請至少選擇一項主要用途");
    return;
  }
  if (applyForm.value.systems.length === 0) {
    message.error("請至少選擇一項預計使用的系統");
    return;
  }
  if (!applyForm.value.ndaFile) {
    message.error("請上傳簽署完成的 NDA 文件");
    return;
  }
  if (!applyForm.value.isNdaChecked) {
    message.error("請同意免責聲明條款");
    return;
  }

  if (forceSubmit !== true) {
    applySubmitting.value = true;
    try {
      const checkRes = await contentAPI.checkApplicationOrganization(
        applyForm.value.organization
      );
      if (checkRes?.data?.exists) {
        applySubmitting.value = false;
        Modal.confirm({
          title: "單位已申請過",
          content: "該單位已申請過API串接金鑰，是否繼續申請？",
          okText: "繼續申請",
          cancelText: "取消",
          onOk: () => {
            submitApplicationForm(true);
          },
        });
        return;
      }
    } catch (err) {
      console.error("Failed to check organization", err);
    }
    applySubmitting.value = false;
  }

  applySubmitting.value = true;

  try {
    const formData = new FormData();
    formData.append("real_name", applyForm.value.realName);
    formData.append("job_title", applyForm.value.jobTitle);
    formData.append("organization", applyForm.value.organization);
    formData.append("use_mode", applyForm.value.useMode);

    applyForm.value.purposes.forEach((p) => {
      formData.append("purposes[]", p);
    });
    applyForm.value.systems.forEach((s) => {
      formData.append("systems[]", s);
    });
    formData.append("nda_file", applyForm.value.ndaFile);

    await contentAPI.submitApplication(formData);
    message.success("申請已成功送出！請靜候審核");
    isApplyModalOpen.value = false;
    await fetchApplicationStatus();
  } catch (err) {
    console.error("Failed to submit application", err);
    const msg =
      err?.response?.data?.message || "送出失敗，請確認填寫內容與檔案格式";
    if (err?.response?.status === 403 && (err?.response?.data?.content?.code === 'ADDITIONAL_TERMS_REQUIRED' || msg.includes('附加條款'))) {
      message.warning("請先閱讀並同意最新版附加條款");
      router.push({
        path: "/additional-terms",
        query: { redirect: router.currentRoute.value.fullPath },
      });
      return;
    }
    message.error(msg);
  } finally {
    applySubmitting.value = false;
  }
};

const disclaimerStorageKey = `dataset_api_license_accepted_${STANDARD}`;

const openUseGuide = async (item) => {
  if (!isLoggedIn.value) {
    goToLogin();
    return;
  }

  let user = null;
  try {
    const meRes = await authAPI.me();
    if (meRes?.data) {
      user = meRes.data;
      localStorage.setItem("user_data", JSON.stringify(user));
    }
  } catch (e) {
    const rawUser = localStorage.getItem("user_data");
    try {
      user = rawUser ? JSON.parse(rawUser) : null;
    } catch (err) { }
  }

  const isAgreed = user?.standards_terms_agreed ? user.standards_terms_agreed['snomed_ct'] : user?.additional_terms_agreed;
  if (!isAgreed) {
    message.warning("閱讀使用說明前，請先同意最新版附加條款");
    router.push({
      path: "/additional-terms",
      query: { redirect: router.currentRoute.value.fullPath },
    });
    return;
  }

  selectedAnnouncement.value = item;
  if (localStorage.getItem(disclaimerStorageKey) === "true") {
    isApiDocsOpen.value = true;
  } else {
    isDisclaimerOpen.value = true;
  }
};

const acceptDisclaimer = () => {
  localStorage.setItem(disclaimerStorageKey, "true");
  isDisclaimerOpen.value = false;
  isApiDocsOpen.value = true;
};

const apiEndpoint = computed(() => {
  const id = selectedAnnouncement.value?.id || "";
  return `${API_URL.replace(
    /\/api$/,
    ""
  )}/api/${STANDARD}/datasets/access/${id}`;
});

const apiParameters = computed(() => {
  const stdCode =
    STANDARD === "loinc"
      ? "loinc_code"
      : STANDARD === "rxnorm"
        ? "rxnorm_code"
        : "snomed_code";
  const stdLabel =
    STANDARD === "loinc"
      ? "LOINC"
      : STANDARD === "rxnorm"
        ? "RxNorm"
        : "SNOMED";
  const stdExample =
    STANDARD === "loinc"
      ? "2750-8"
      : STANDARD === "rxnorm"
        ? "861214"
        : "91823009";

  return [
    {
      name: "page",
      type: "integer",
      description: "頁碼，從 1 開始。",
      example: "1",
    },
    {
      name: "per_page",
      type: "integer",
      description: "每頁筆數，預設 15。",
      example: "15",
    },
    {
      name: "nhi_code",
      type: "string",
      description: "依健保碼篩選，支援模糊查詢。",
      example: "06001C",
    },
    {
      name: stdCode,
      type: "string",
      description: `依 ${stdLabel} 碼篩選，支援模糊查詢。`,
      example: stdExample,
    },
  ];
});

const apiResponseExample = JSON.stringify(
  {
    status: "success",
    message: "",
    content: {
      data: [
        {
          nhi_code: "06001C",
          nhi_test_item_name: "pH",
          hospital_test_item_name: "pH",
          unit: "",
          specimen_type: "Pleural fluid",
          loinc_code: "2750-8",
          component: "pH",
          property: "LsCnc",
          time_aspect: "Pt",
          system: "Plr fld",
          scale: "SemiQn",
          method: "",
        },
      ],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 1,
        filters: {
          unit: [],
          specimenType: [],
          system: [],
          scale: [],
        },
      },
    },
  },
  null,
  2
);

const goToLogin = () => {
  router.push({
    path: "/sign-in",
    query: { redirect: router.currentRoute.value.fullPath },
  });
};

const goToRegister = () => {
  router.push({
    path: "/register",
    query: { redirect: router.currentRoute.value.fullPath },
  });
};

function updateScrollState() {
  const el = contentRef.value;
  if (!el) return;
  canScrollRight.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
  isScrollable.value = el.scrollWidth > el.clientWidth;
}

function normalizeTableRow(item) {
  return {
    id: item?.id,
    spec: item?.title || "",
    catZh: item?.category || "",
    catEn: item?.category_en || "",
    unit: item?.organization || "",
    releaseVersion: item?.release_version || "-",
    publishedAt: item?.published_at || "",
    sourceFilePath: item?.source_file_path || "",
    importStatus: item?.import_status || "",
  };
}

const categoryOptions = computed(() => {
  const set = new Set();
  for (const item of announcements.value) {
    if (item?.category) set.add(item.category);
  }
  return Array.from(set);
});

const unitOptions = computed(() => {
  const set = new Set();
  for (const item of announcements.value) {
    if (item?.organization) set.add(item.organization);
  }
  return Array.from(set);
});

const tableData = computed(() => {
  const q = String(searchQuery.value || "")
    .trim()
    .toLowerCase();
  const cat = category.value;
  const org = unit.value;

  return announcements.value
    .filter((item) => {
      if (cat !== "all" && item?.category !== cat) return false;
      if (org !== "all" && item?.organization !== org) return false;

      if (!q) return true;
      return (
        String(item?.title || "")
          .toLowerCase()
          .includes(q) ||
        String(item?.category || "")
          .toLowerCase()
          .includes(q) ||
        String(item?.organization || "")
          .toLowerCase()
          .includes(q)
      );
    })
    .map(normalizeTableRow);
});

onMounted(async () => {
  isLoading.value = true;
  errorMessage.value = "";
  fetchApplicationStatus();

  try {
    const res = await contentAPI.announcements({ status: "published" });
    announcements.value = Array.isArray(res?.data) ? res.data : [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || "系統發生錯誤，請稍後再試";
    announcements.value = [];
  } finally {
    isLoading.value = false;
  }

  await nextTick();
  updateScrollState();

  const el = contentRef.value;
  if (el) {
    el.addEventListener("scroll", updateScrollState);
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }
  }
  window.addEventListener("resize", updateScrollState);
});

onBeforeUnmount(() => {
  const el = contentRef.value;
  if (el) {
    el.removeEventListener("scroll", updateScrollState);
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  window.removeEventListener("resize", updateScrollState);
});
</script>

<style scoped lang="scss">
.standard-announcements-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.filters-container {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  align-items: center;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 14px;
    color: #333;
  }
}

.search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  box-sizing: border-box;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: #696969;
    pointer-events: none;

    svg {
      width: 20px !important;
      height: 20px !important;
      display: block;
    }
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    outline: none;
    padding: 0 12px 0 38px;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    box-sizing: border-box;

    &::placeholder {
      color: #696969;
    }
  }
}

.custom-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 150px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;

  .select-label {
    font-size: 16px !important;
    color: #1a1a1a;
    white-space: nowrap;
    user-select: none;
  }

  .select-divider {
    margin: 0 8px;
    color: #b4b4b4;
    font-size: 16px !important;
    user-select: none;
  }

  select.select-input {
    flex: 1;
    height: 100%;
    border: none;
    background: transparent !important;
    outline: none;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    cursor: pointer;
    padding-right: 20px;
    appearance: none;
    -webkit-appearance: none;

    option {
      color: #1a1a1a;
      background: #ffffff;
      font-size: 16px !important;
    }
  }

  .select-arrow {
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
}

.materials-content {
  background-color: #fff;
  border-radius: 8px;
  overflow-x: auto;
  border: 1px solid #eaeaea;
}

.materials-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.materials-table th,
.materials-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #eaeaea;
}

.materials-table th {
  background-color: #f8f9fa;
  color: #333;
  font-weight: 600;
  font-size: 14px;
}

.materials-table td {
  color: #333;
  font-size: 16px;
  vertical-align: middle;
}

.materials-table tbody tr:last-child td {
  border-bottom: none;
}

.col-spec {
  width: 30%;
}

.col-cat {
  width: 20%;
}

.col-unit {
  width: 15%;
}

.col-release {
  width: 15%;
}

.col-link {
  width: 20%;
}

.cat-zh {
  font-size: 16px;
  color: #333;
}

.cat-en {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.release-version {
  font-size: 16px;
  color: #333;
}

.published-at {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.use-guide-btn {
  font-weight: 500;
  color: #022964;
  padding: 0;
  height: auto;
  font-size: 16px;

  &:hover {
    color: #1171dd;
  }
}

.login-prompt {
  text-align: center;
  padding: 24px 16px;

  .prompt-icon {
    font-size: 48px;
    color: #022964;
    margin-bottom: 16px;
  }

  .prompt-intro {
    font-size: 16px;
    color: #374151;
    margin-bottom: 24px;
    line-height: 1.6;
  }
}

.guide-section {
  text-align: left;
  margin-bottom: 20px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #4b5563;
    line-height: 1.5;
  }

  ol {
    padding-left: 20px;
    color: #4b5563;
    font-size: 14px;
    margin-top: 8px;

    li {
      margin-bottom: 6px;
    }
  }
}

.api-docs-content {
  color: #374151;
  line-height: 1.7;
}

.api-docs-label {
  margin: 0 0 8px;
  font-weight: 600;
  color: #111827;
}

.api-docs-content pre {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  white-space: pre-wrap;
  word-break: break-word;
}

.api-docs-method {
  display: inline-block;
  margin-right: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #1d4ed8;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  vertical-align: middle;
}

.api-docs-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 16px;
  font-size: 14px;
}

.api-docs-table th,
.api-docs-table td {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: top;
  color: #111827;
}

.api-docs-table th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #4b5563;
}

.api-docs-table code {
  padding: 2px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  font-size: 13px;
}

.disclaimer-content {
  color: #374151;
  line-height: 1.6;

  ol {
    padding-left: 20px;
    margin: 12px 0;

    li {
      margin-bottom: 8px;
    }
  }
}

@media (max-width: 768px) {

  .materials-table th,
  .materials-table td {
    padding: 16px 12px;
  }

  .materials-table {
    min-width: 750px;
  }

  .materials-table th:first-child,
  .materials-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 10;
  }

  .materials-table th:first-child {
    background-color: #f8f9fa;
  }

  .materials-table td:first-child {
    background-color: #fff;
  }

  .materials-content[data-can-scroll-right="true"] .materials-table th:first-child::after,
  .materials-content[data-can-scroll-right="true"] .materials-table td:first-child::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0px;
    bottom: 0;
    width: 5px;
    pointer-events: none;
    box-shadow: 2px 0px 5px rgba(0, 0, 0, 0.08);
  }

  .col-spec {
    width: 220px;
    min-width: 220px;
  }

  .col-cat {
    width: 150px;
    min-width: 150px;
  }

  .col-unit {
    width: 120px;
    min-width: 120px;
  }

  .col-release {
    width: 120px;
    min-width: 120px;
  }

  .col-link {
    width: 140px;
    min-width: 140px;
  }

  .filters-container {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .filter-item {
    flex: 1 1 calc(50% - 6px);
  }

  .filter-item:first-child {
    flex: 1 1 100%;
  }

  .filter-item .ant-input,
  .filter-item .ant-select {
    width: 100% !important;
  }
}

.scroll-hint {
  display: none;
}

@media (max-width: 768px) {
  .scroll-hint {
    display: inline-flex;
    align-items: center;
    background: rgba(2, 41, 100, 0.08);
    color: #022964;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    gap: 8px;
    animation: pulse-bg 2s infinite;

    .scroll-hint-icon {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      animation: bounce-right 1.5s infinite;
    }
  }
}

@keyframes bounce-right {

  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(4px);
  }
}

@keyframes pulse-bg {

  0%,
  100% {
    background-color: rgba(0, 121, 187, 0.08);
  }

  50% {
    background-color: rgba(0, 121, 187, 0.16);
  }
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none !important;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }
}

.mobile-state-placeholder {
  text-align: center;
  padding: 40px 20px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  color: #666;
  font-size: 16px;
}

.materials-cards {
  display: flex;
  flex-direction: column;
  width: 100%;

  .cat-zh {
    font-size: 15px;
    color: #333;
  }

  .cat-en {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }

  .release-version {
    font-size: 15px;
    color: #333;
  }

  .published-at {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }
}
</style>
